import React, { useState } from 'react';
import { Global, css } from '@emotion/react';
// import { FieldArray } from 'react-final-form-arrays';
// import { Form, FormSpy } from 'react-final-form';
// import arrayMutators from 'final-form-arrays';
import useForceUpdateOnWindowResize from '@zola/zola-ui/src/hooks/useForceUpdateOnWindowResize';

// Types
import type {
  WThemeGroupFontFamilyView,
  WPublicWeddingView,
  WThemeComponentColorView,
} from '@zola/svc-web-api-ts-client';
import type { BaseLayoutProps } from 'components/publicWebsiteV2/pageLayouts/types';

// Components
// import { CheckboxList } from '@zola/zola-ui/src/components/Form/CheckboxList';
import SiteHeader from 'components/publicWebsiteV2/common/Header';
import ModalRoot from 'components/common/ModalRoot';

// Util
import {
  ThemeContext,
  DEFAULT_HEADER_FONT_VALUES,
  DEFAULT_BODY_FONT_VALUES,
  DEFAULT_ACCENT_COLOR,
} from 'components/publicWebsiteV2/context';
import {
  mapButtonStylesToComponent,
  mapLinkStylesToComponent,
  mapBackgroundToCSS,
  fillFontProperties,
  mapFullFontViewToSerializedStyles,
} from 'components/publicWebsiteV2/util/mappers';
import { mapModeThemeToInputField } from 'components/publicWebsiteV2/util/genericFormElements.styles';
// import { COMPONENTS } from 'components/publicWebsiteV2/util/componentConstants';
import { isDarkColor } from 'pages/publicStyleUtils/utils.styles';
import UnifiedNav from 'components/common/header/UnifiedNav';
import Metadata from 'components/publicWebsiteV2/common/Metadata';
import { mapWeddingAccountToWebsiteDateProp } from 'util/websiteDateUtil';

import fontAssets from 'cards/stylesheets/_fonts.styles';
import { Container } from './PublicV2.styles';

type PublicV2Props = BaseLayoutProps;

const PublicV2: React.FC<PublicV2Props> = ({ wedding, children }) => {
  useForceUpdateOnWindowResize();
  const { title, nav_items, slug, city, state_province } = wedding || {};
  const websiteDate = wedding && mapWeddingAccountToWebsiteDateProp(wedding, undefined);
  // font families for theme
  const globalHeaderFontFamily = wedding?.public_theme_v2?.theme_group_font_families
    .HEADER as WThemeGroupFontFamilyView;
  const globalBodyFontFamily = wedding?.public_theme_v2?.theme_group_font_families
    .BODY as WThemeGroupFontFamilyView;

  // properties from global component
  const { fonts, background, accent_color: accentColor } =
    wedding?.public_theme_v2?.components?.GLOBAL || {};
  const globalHeaderFont = fonts?.HEADER?.[1];
  const globalBodyFont = fonts?.BODY?.[1];
  const backgroundColor = background?.background_color;

  // create data structure to hold global fallback values
  // supplements values if global not provided
  const globalHeaderFontValues = fillFontProperties(DEFAULT_HEADER_FONT_VALUES, {
    ...globalHeaderFontFamily,
    ...globalHeaderFont,
  });
  const globalHeaderFontStyle = mapFullFontViewToSerializedStyles(globalHeaderFontValues);

  const globalBodyFontValues = fillFontProperties(DEFAULT_BODY_FONT_VALUES, {
    ...globalBodyFontFamily,
    ...globalBodyFont,
  });
  const globalBodyFontStyle = mapFullFontViewToSerializedStyles(globalBodyFontValues);

  const light_or_dark: 'LIGHT' | 'DARK' = isDarkColor(backgroundColor?.value || 'FFFFFF')
    ? 'DARK'
    : 'LIGHT';
  const cmsEntityComponent = wedding?.public_theme_v2?.components?.CMS_ENTITY;
  const cmsEntityComponentHeaderFontValues = fillFontProperties(
    globalHeaderFontValues,
    cmsEntityComponent?.fonts?.HEADER?.[1]
  );
  const cmsEntityComponentHeaderFontStyles = mapFullFontViewToSerializedStyles(
    cmsEntityComponentHeaderFontValues
  );
  const cmsEntityComponentBodyFontValues = fillFontProperties(
    globalBodyFontValues,
    cmsEntityComponent?.fonts?.BODY?.[1]
  );
  const cmsEntityComponentBodyFontStyles = mapFullFontViewToSerializedStyles(
    cmsEntityComponentBodyFontValues
  );
  const globalInputFieldStyles = mapModeThemeToInputField(
    light_or_dark,
    cmsEntityComponentBodyFontValues.color
  );
  const Button = mapButtonStylesToComponent(globalBodyFontFamily?.font_family?.name, accentColor);
  const CmsEntityBodyLink = mapLinkStylesToComponent(
    cmsEntityComponentBodyFontValues,
    light_or_dark,
    backgroundColor as WThemeComponentColorView
  );
  const [context] = useState({
    components: {
      globalHeaderFontStyle,
      globalBodyFontStyle,
      globalHeaderFontValues,
      globalBodyFontValues,
      globalAccentColor: accentColor || DEFAULT_ACCENT_COLOR,
      globalInputFieldStyles,
      Button,
      CmsEntityBodyLink,
      cmsEntityComponentHeaderFontValues,
      cmsEntityComponentBodyFontValues,
      cmsEntityComponentHeaderFontStyles,
      cmsEntityComponentBodyFontStyles,
    },
    light_or_dark,
    highlightedComponents: [],
    wedding,
  });

  const globalStyles = css`
    html {
      body {
        ${mapBackgroundToCSS(background)}
        ${fontAssets}

        .modal-xl {
          width: 1020px;
        }
      }
    }
  `;

  // const componentListOptions = Object.keys(COMPONENTS).map(c => ({
  //   value: c,
  //   // @ts-ignore
  //   label: COMPONENTS[c],
  // }));

  return (
    <Container>
      <Metadata wedding={wedding as WPublicWeddingView} />
      <Global styles={globalStyles} />
      <UnifiedNav isHidden />
      {/* @ts-ignore */}
      <ModalRoot />
      <ThemeContext.Provider value={context}>
        <SiteHeader
          title={title}
          navItems={nav_items}
          slug={slug}
          websiteDate={websiteDate}
          city={city}
          stateProvince={state_province}
        />
        {children}
        {/* <div
          style={{
            backgroundColor: 'white',
            width: '300px',
            border: '1px solid black',
            padding: '20px',
            marginTop: '20px',
          }}
        >
          Component Key
          <Form
            // eslint-disable-next-line
            onSubmit={v => console.log(v)}
            mutators={{
              ...arrayMutators,
            }}
            render={() => (
              <>
                <FieldArray name="components" options={componentListOptions}>
                  {({ fields, meta }) => (
                    <CheckboxList fields={fields} meta={meta} options={componentListOptions} />
                  )}
                </FieldArray>
                <FormSpy
                  subscription={{ values: true }}
                  onChange={({ values }) =>
                    setContext({ ...context, highlightedComponents: values.components })
                  }
                />
              </>
            )}
          />
        </div> */}
      </ThemeContext.Provider>
    </Container>
  );
};

export default PublicV2;
